import { unassignRoleSuccess } from './../../../settings/state/actions/index';
import { addUserSuccess, removeUserSuccess } from './actions/index';
import { createReducer } from '@reduxjs/toolkit';
import { State } from 'state';
import * as actions from './actions';
import { IUserList, TModerationUser, User, UserRole } from './types';

const { fetchUsersStart, fetchUsersFailure, fetchUsersSuccess } = actions;

export type IModeratedUsersState = Record<TModerationUser, IUserList>;

const initialState = {
  [TModerationUser.AllowedUsers]: {
    isLoading: false,
    users: [],
    offset: 0,
    count: 500
  },
  [TModerationUser.BlockedUsers]: {
    isLoading: false,
    users: [],
    offset: 0,
    count: 500
  },
  [TModerationUser.BlockedIps]: {
    isLoading: false,
    users: [],
    offset: 0,
    count: 500
  },
  [TModerationUser.TeamMembers]: {
    isLoading: false,
    isFetched: false,
    users: []
  }
};

const moderatedUsersReducer = createReducer(initialState, {
  ['SPOTS/CHANGE_SPOT']: state => {
    return initialState;
  },
  [fetchUsersStart.type]: (state, { payload: { type } }) => {
    const userListType = getUserListType(state, type);

    userListType.isLoading = true;
  },
  [fetchUsersSuccess.type]: (state, { payload: { type, users, ips, offset, count } }) => {
    const userListType = getUserListType(state, type);
    const itemsToAdd = isIpsType(type) ? ips : users;

    userListType.isLoading = false;
    userListType.users = createUserListByType(state, type, itemsToAdd);
    userListType.offset = offset;
    userListType.count = count;
    userListType.isFetched = true;
  },
  [fetchUsersFailure.type]: (state, { payload: { type } }) => {
    const userListType = getUserListType(state, type);

    userListType.isLoading = false;
  },
  [addUserSuccess.type]: (state, { payload: { type, user, ip } }) => {
    if (!type) {
      return state;
    }

    const userListType = getUserListType(state, type);
    const itemsToAdd = isIpsType(type) ? [{ ip }] : [user];

    userListType.users = createUserListByType(state, type, itemsToAdd);
  },
  [removeUserSuccess.type]: (state, { payload: { type, user, ip } }) => {
    const userListType = getUserListType(state, type);
    const itemToRemove = isIpsType(type) ? ip : user?.id;

    userListType.users = userListType.users.filter(user => itemToRemove !== user);
  },
  [unassignRoleSuccess.type]: (state, { payload: { userId, roles = [] } }) => {
    const isRemovedFromTeam = roles.includes('team_member');

    if (isRemovedFromTeam) {
      state[TModerationUser.TeamMembers].users.filter(currUserId => currUserId !== userId);
    }
  }
});

function isIpsType(type: TModerationUser) {
  return type === TModerationUser.AllowedIps || type === TModerationUser.BlockedIps;
}

function getUserListType(state, type: TModerationUser) {
  let userListType = state[type];

  if (!userListType) {
    throw new Error(`Unknown moderation user type: ${type}`);
  }

  return userListType;
}

function createUserListByType(state, type, users) {
  const fetchedUsers = state[type].users;

  return [
    ...users
      .map(user => {
        let newUserIdOrIp;
        switch (type) {
          case TModerationUser.AllowedUsers:
          case TModerationUser.BlockedUsers:
          case TModerationUser.TeamMembers:
            newUserIdOrIp = user.id ?? user.userId;
            break;
          case TModerationUser.BlockedIps:
          case TModerationUser.AllowedIps:
            newUserIdOrIp = user.ip;
            break;
        }

        if (!fetchedUsers.includes(newUserIdOrIp)) {
          return newUserIdOrIp;
        }
      })
      .filter(Boolean),
    ...fetchedUsers
  ];
}

function sortListByType(type) {
  switch (type) {
    case TModerationUser.TeamMembers:
      return (a: User, b: User) => User.isStrongerRole(a, b);
    default:
      return (a, b) => 1;
  }
}

function getUserOfType(globalUsers: Record<string, User>, type: TModerationUser) {
  // When reading the user to the connected component we can do some manipulations here
  // in order to support the same structure the component is expected.
  return (userIdOrIp: string) => {
    switch (type) {
      case TModerationUser.AllowedUsers:
      case TModerationUser.BlockedUsers:
        return globalUsers[userIdOrIp];
      case TModerationUser.TeamMembers:
        return getTeamMemberUser(globalUsers, userIdOrIp);
      case TModerationUser.BlockedIps:
      case TModerationUser.AllowedIps:
        return userIdOrIp;
    }
  };
}

function getTeamMemberUser(globalUsers: Record<string, User>, userId: string) {
  const user = globalUsers[userId];

  if (!user) {
    console.error(`Unable to find user with id: ${userId}`);
    return null;
  }

  // We don't want to expose super admins on team page
  const validRoles = user.get('roles', []).filter(role => role.toLowerCase() !== 'super_admin');
  const userRole = user.getUserRole(validRoles);
  const updatedUser = user.set('roles', validRoles).set('type', userRole) as User;

  if (UserRole[userRole.toUpperCase()] < UserRole.TEAM_MEMBER) {
    return null;
  }

  return updatedUser;
}

// ---------- State Selectors ---------- //

export function getUsersOfType(
  state: State,
  type: TModerationUser,
  options: { filterBy?: (user: User) => boolean } = {}
) {
  const moderatedUserState = state.moderation.moderatedUsers[type] ?? { users: null };
  const globalUsersState = state.moderation.users.users;
  const userList = moderatedUserState.users ?? [];

  return {
    ...moderatedUserState,
    users: userList
      .map(getUserOfType(globalUsersState, type))
      .filter(options.filterBy ?? Boolean)
      .sort(sortListByType(type))
  };
}

export function isTeamMembersFetched(state: State) {
  return state?.moderation?.moderatedUsers?.[TModerationUser.TeamMembers].isFetched ?? false;
}

export { moderatedUsersReducer };
